import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Coins, Crown, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const T = {
  ro: {
    eyebrow: "Prețuri & Planuri", title: "Alege planul potrivit pentru afacerea ta",
    sub: "Fără costuri ascunse. Începe gratuit, treci la Pro când ai nevoie de mai mult.",
    free: "Gratuit", freeSub: "Pentru a explora platforma",
    pro: "OXO Pro", per: "/ 30 zile", popular: "Cel mai popular",
    tokens: "Pachete de tokeni AI", tokensSub: "Pentru calcule inginerești avansate cu Emmy",
    freeFeatures: ["Navigare catalog cu 1.400+ produse", "Chat AI de bază cu Emmy", "Până la 3 anunțuri active", "Contact vânzători"],
    proFeatures: ["Anunțuri nelimitate", "Badge Companie Verificată", "Acces complet OXO Radar", "Instrumente inginerești deblocate", "10 tokeni AI bonus / lună", "Prioritate în listări"],
    buyPro: "Abonează-te la Pro", buyTokens: "Cumpără", credits: "credite", getStarted: "Începe gratuit",
    loginFirst: "Autentifică-te pentru a continua", note: "Plăți securizate prin Stripe (mod test).",
    current: "Planul tău actual",
  },
  hu: {
    eyebrow: "Árak & Csomagok", title: "Válaszd a vállalkozásodhoz illő csomagot",
    sub: "Nincsenek rejtett költségek. Kezdd ingyen, válts Pro-ra, amikor többre van szükséged.",
    free: "Ingyenes", freeSub: "A platform felfedezéséhez",
    pro: "OXO Pro", per: "/ 30 nap", popular: "Legnépszerűbb",
    tokens: "AI token-csomagok", tokensSub: "Fejlett mérnöki számításokhoz Emmyvel",
    freeFeatures: ["Katalógus böngészés 1.400+ termékkel", "Alap AI chat Emmyvel", "Max. 3 aktív hirdetés", "Eladók elérése"],
    proFeatures: ["Korlátlan hirdetés", "Ellenőrzött Cég badge", "Teljes OXO Radar hozzáférés", "Feloldott mérnöki eszközök", "10 bónusz AI token / hó", "Kiemelt listázás"],
    buyPro: "Előfizetek a Pro-ra", buyTokens: "Megveszem", credits: "kredit", getStarted: "Kezdd ingyen",
    loginFirst: "Jelentkezz be a folytatáshoz", note: "Biztonságos fizetés Stripe-on (teszt mód).",
    current: "Jelenlegi csomagod",
  },
  en: {
    eyebrow: "Pricing & Plans", title: "Choose the right plan for your business",
    sub: "No hidden fees. Start free, upgrade to Pro when you need more.",
    free: "Free", freeSub: "To explore the platform",
    pro: "OXO Pro", per: "/ 30 days", popular: "Most popular",
    tokens: "AI Token Packages", tokensSub: "For advanced engineering calculations with Emmy",
    freeFeatures: ["Browse catalog with 1,400+ products", "Basic AI chat with Emmy", "Up to 3 active listings", "Contact sellers"],
    proFeatures: ["Unlimited listings", "Verified Company Badge", "Full OXO Radar access", "Unlocked engineering tools", "10 bonus AI tokens / mo", "Priority listing"],
    buyPro: "Subscribe to Pro", buyTokens: "Buy", credits: "credits", getStarted: "Start free",
    loginFirst: "Sign in to continue", note: "Secure payments via Stripe (test mode).",
    current: "Your current plan",
  },
};

export default function Pricing() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lng = (i18n.language || "ro").slice(0, 2);
  const t = T[lng] || T.ro;
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const isPro = !!user?.is_premium;

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("membership");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success(lng === "hu" ? "OXO Pro aktiválva! 🎉" : lng === "en" ? "OXO Pro activated! 🎉" : "OXO Pro activat! 🎉");
          window.history.replaceState({}, "", "/preturi");
          return;
        }
      } catch { /* ignore */ }
      if (tries++ < 6) setTimeout(poll, 1600);
    };
    poll();
  }, [lng]);

  const ref = () => localStorage.getItem("oxo_ref") || null;

  const subscribePro = async () => {
    if (!user) { toast.info(t.loginFirst); navigate("/autentificare"); return; }
    setBusy("pro");
    try {
      const r = await api.post("/membership/checkout", { origin_url: window.location.origin, referral_id: ref() });
      window.location.href = r.data.url;
    } catch { toast.error("Eroare la inițierea plății."); setBusy(""); }
  };

  const buyTokens = async (pack) => {
    if (!user) { toast.info(t.loginFirst); navigate("/autentificare"); return; }
    setBusy(pack);
    try {
      const r = await api.post("/tokens/checkout", { pack, origin_url: window.location.origin, referral_id: ref() });
      window.location.href = r.data.url;
    } catch { toast.error("Eroare la inițierea plății."); setBusy(""); }
  };

  const eur = (n) => `€${Math.round(n)}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-600">{t.eyebrow}</span>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mt-3">{t.title}</h1>
        <p className="text-slate-500 mt-4 text-base">{t.sub}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-14 max-w-4xl mx-auto">
        {/* Free */}
        <div className="border border-slate-200 p-8 flex flex-col" data-testid="plan-free">
          <div className="flex items-center gap-2 text-slate-900"><Sparkles className="w-5 h-5 text-slate-400" /><span className="font-black text-xl">{t.free}</span></div>
          <p className="text-sm text-slate-500 mt-1">{t.freeSub}</p>
          <div className="mt-5"><span className="text-4xl font-black">€0</span></div>
          <ul className="mt-6 space-y-3 flex-1">
            {t.freeFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>
            ))}
          </ul>
          <button onClick={() => navigate(user ? "/anunturi" : "/inregistrare")} className="mt-8 w-full border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-5 py-3 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="plan-free-cta">
            {t.getStarted}
          </button>
        </div>

        {/* Pro */}
        <div className="relative border-2 border-orange-600 p-8 flex flex-col shadow-xl" data-testid="plan-pro">
          <span className="absolute -top-3 left-8 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1">{t.popular}</span>
          <div className="flex items-center gap-2 text-orange-600"><Crown className="w-5 h-5" /><span className="font-black text-xl">{t.pro}</span></div>
          <div className="mt-5 flex items-end gap-1">
            <span className="text-4xl font-black">{eur(pricing?.pro?.price_eur ?? 15)}</span>
            <span className="text-slate-400 font-semibold mb-1">{t.per}</span>
          </div>
          <ul className="mt-6 space-y-3 flex-1">
            {t.proFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-800 font-medium"><Check className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />{f}</li>
            ))}
          </ul>
          {isPro ? (
            <div className="mt-8 w-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-5 py-3 font-bold uppercase tracking-wide text-sm text-center" data-testid="plan-pro-current">{t.current}</div>
          ) : (
            <button onClick={subscribePro} disabled={busy === "pro"} className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 font-bold uppercase tracking-wide text-sm transition-colors disabled:opacity-50" data-testid="plan-pro-cta">
              {busy === "pro" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} {t.buyPro}
            </button>
          )}
        </div>
      </div>

      {/* Tokens */}
      <div className="mt-16 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 justify-center"><Coins className="w-5 h-5 text-amber-500" /><h2 className="font-black text-2xl tracking-tight">{t.tokens}</h2></div>
        <p className="text-center text-slate-500 text-sm mt-2">{t.tokensSub}</p>
        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          {(pricing?.token_packs || []).map((p) => (
            <div key={p.key} className="border border-slate-200 p-6 text-center hover:border-amber-400 transition-colors" data-testid={`token-pack-${p.key}`}>
              <div className="font-black text-lg">{p.label}</div>
              <div className="text-3xl font-black text-amber-600 mt-2">{p.credits}</div>
              <div className="text-xs uppercase tracking-wide text-slate-400">{t.credits}</div>
              <div className="mt-3 text-slate-900 font-bold">${p.price_usd} <span className="text-slate-400 font-mono text-xs">≈ {eur(p.price_eur)}</span></div>
              <button onClick={() => buyTokens(p.key)} disabled={busy === p.key} className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white px-4 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors disabled:opacity-50" data-testid={`token-buy-${p.key}`}>
                {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {t.buyTokens}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-6">{t.note}</p>
      </div>
    </div>
  );
}
